import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';
import { HHDSImageTagName } from '../Image/Image';
import gsap from 'gsap';
import { rosAnimation, STAnimationType } from '../../utils/ScrollTriggerHelper';

const DEBUG_VERBOSE: boolean = false;
export const HHDSImageCompareTagName: string = 'hhds-image-compare';
const TAG_NAME: string = HHDSImageCompareTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

export const HHDSImageCompareAttrNames = {
	header: 'header',
	leftSrcset: 'left-srcset',
	leftSizes: 'left-sizes',
	leftSrc: 'left-src',
	leftAlt: 'left-alt',
	leftSubtitle: 'left-subtitle',
	rightSrc: 'right-src',
	rightSrcset: 'right-srcset',
	rightSizes: 'right-sizes',
	rightAlt: 'right-alt',
	rightSubtitle: 'right-subtitle',
	rosAnimation: 'ros-animation',
};

const Attrs = HHDSImageCompareAttrNames;

export class HHDSImageCompare extends Component {
	private leftImageContainer!: HTMLElement;
	private rightImageContainer!: HTMLElement;
	private imagesContainer!: HTMLElement;

	private imagesContainerTween: gsap.core.Tween | null = null;

	constructor() {
		super();
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		const headerHtml = this.htmlIfVar(Attrs.header, (text) => {
			return `<div class="${TAG_NAME}__header-container"><h5 class="${TAG_NAME}__header heading heading--05 animate">${text.toUpperCase()}</h5></div>`;
		});

		const subtitle = (text: string) => `<h5 class="${TAG_NAME}__subtitle-text animate heading heading--05">${text.toUpperCase()}</h5>`;

		const leftSubtitleHtml = this.htmlIfVar(Attrs.leftSubtitle, (text) => {
			return `<div class="${TAG_NAME}__subtitle-left">${subtitle(text)}</div>`;
		});
		const rightSubtitleHtml = this.htmlIfVar(Attrs.rightSubtitle, (text) => {
			return `<div class="${TAG_NAME}__subtitle-right">${subtitle(text)}</div>`;
		});

		const html: string = `
      <div class="${TAG_NAME}">
				<div class="container">
					<div class="grid">
						<div class="col-span-4 md:col-span-12 lg:col-start-3 lg:col-span-20">
							${headerHtml}
							<div class="${TAG_NAME}__images-container">
								<div class="${TAG_NAME}__left-image-container">${this.imageHtml(Attrs.leftSrc, Attrs.leftSrcset, Attrs.leftSizes, Attrs.leftAlt)}</div>
								<div class="${TAG_NAME}__right-image-container">${this.imageHtml(Attrs.rightSrc, Attrs.rightSrcset, Attrs.rightSizes, Attrs.rightAlt)}</div>
							</div>
						</div>
					</div>
					<div class="grid ${TAG_NAME}__subtitles-container">
						<div class="col-span-1 md:col-span-3 lg:col-span-4 lg:col-start-3">
							${leftSubtitleHtml}
						</div>
						<div class="col-span-1 md:col-span-3 lg:col-span-4 col-start-4 md:col-start-10 lg:col-start-19">
							${rightSubtitleHtml}
						</div>
					</div>
				</div>
			</div>
    `;

		this.setInnerHtml('', '', html);

		this.imagesContainer = this.element(`.${TAG_NAME}__images-container`);
		this.leftImageContainer = this.element(`.${TAG_NAME}__left-image-container`);
		this.rightImageContainer = this.element(`.${TAG_NAME}__right-image-container`);

		if (this.vars.get<boolean>(Attrs.rosAnimation)) {
			gsap.set(this.leftImageContainer, { opacity: 0, y: '5%' });
			gsap.set(this.rightImageContainer, { opacity: 0, y: '5%' });
			this.imagesContainerTween?.kill();

			requestAnimationFrame(() => {
				this.imagesContainerTween = rosAnimation(this.imagesContainer, STAnimationType.triggered, {
					start: '30%',
					end: '100%',
					onStart: () => {
						gsap.to(this.leftImageContainer, {
							opacity: 1,
							duration: 0.8,
							y: '0%',
							ease: 'power2.out',
						});
						gsap.to(this.rightImageContainer, {
							opacity: 1,
							duration: 0.8,
							y: '0%',
							delay: 0.3,
							ease: 'power2.out',
						});
					},
				});
			});
		}

		this.observeSlotChanges(true);
	}

	private imageHtml(srcKey: string, srcsetKey: string, sizesKey: string, altKey: string): string {
		const src = this.vars.get<string>(srcKey);
		const srcset = this.vars.get<string>(srcsetKey);
		const sizes = this.vars.get<string>(sizesKey);
		const alt = this.vars.get<string>(altKey);
		const classAttr: string = `class="${TAG_NAME}__images-container__image"`;
		if (srcset?.length && sizes?.length) {
			return `<${HHDSImageTagName} ${classAttr} srcset="${srcset}" sizes="${sizes}" alt="${alt}"></${HHDSImageTagName}>`;
		} else if (src?.length) {
			return `<${HHDSImageTagName} ${classAttr} src="${src}" alt="${alt}"></${HHDSImageTagName}>`;
		}
		return '';
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
		this.imagesContainerTween?.kill();
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: HTMLElement[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.header]: {
		description: 'Header text.',
		defaultValue: '',
		type: String,
	},
	[Attrs.leftSrc]: {
		description: 'URL for the left image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.leftSrcset]: {
		description: 'Srcset for the left image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.leftSizes]: {
		description: 'Sizes for the left image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.leftAlt]: {
		description: 'Alt text for the left image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.leftSubtitle]: {
		description: 'Subtitle for the left image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.rightSrc]: {
		description: 'URL for the right image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.rightSrcset]: {
		description: 'Srcset for the right image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.rightSizes]: {
		description: 'Sizes for the right image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.rightAlt]: {
		description: 'Alt text for the right image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.rightSubtitle]: {
		description: 'Subtitle for the right image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.rosAnimation]: {
		description: 'Enable scroll-triggered animations.',
		defaultValue: true,
		type: Boolean,
	},
};
